import { useState, useEffect, useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { useAppShellPlatform } from '../../hooks/useAppShellPlatform';
import { useToast } from '../../hooks/useToast';
import { getMyIntel, getKingdomIntel, shareIntelReport, deleteIntelReport } from '../../api/intel';
import { groupIntelByTarget } from '../../utils/groupIntelByTarget';
import type { TargetGroup } from '../../utils/groupIntelByTarget';
import type { TargetSortValue } from '../../utils/intelSort';
import { MobileIntelHubShell } from './MobileIntelHubShell';
import { DesktopIntelHubShell } from './DesktopIntelHubShell';
import type { IntelReportDto } from '../../types/intel';

// Single source of truth — both fetchData and DesktopIntelHubShell's pagination reference this
// one constant (server's MAX_PAGE_SIZE), never a second hardcoded 50.
export const PAGE_SIZE = 100;

interface IntelHubTabProps {
  worldId: string;
  provinceId: string;
  reports: IntelReportDto[];
  onReports: Dispatch<SetStateAction<IntelReportDto[]>>;
  loaded: boolean;
  onLoaded: (loaded: boolean) => void;
}

/**
 * Fully-enumerated shell props both `MobileIntelHubShell`/`DesktopIntelHubShell` consume
 * (`RelationsShellProps`/`DragonsShellProps` precedent). Deliberately does not include a raw
 * `reports` prop — every gate a shell needs is already expressible via `targetGroups.length`/
 * `crystalEyeReports.length`.
 */
export interface IntelHubShellProps {
  worldId: string;
  provinceId: string;
  targetGroups: TargetGroup[];
  crystalEyeReports: IntelReportDto[];
  sortKey: TargetSortValue;
  onSortKey: (value: TargetSortValue) => void;
  scope: 'mine' | 'kingdom';
  onScope: (scope: 'mine' | 'kingdom') => void;
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  handleShare: (reportId: string) => void;
  handleDelete: (reportId: string) => void;
  onLoadMore: () => void;
  onRefresh: () => void;
  refetchTarget: (targetProvinceId: string) => void;
  // Desktop-only Previous/Next pagination.
  page: number;
  onPage: (page: number) => void;
}

/**
 * Merges `incoming` into `existing`, keyed on `report.id` — last-write-wins (an `incoming` report
 * replaces an `existing` one with the same id in place; a genuinely new id is appended). Guards
 * against duplicate ids from two reachable flows: a gather inserting a new report at the front of
 * the server's ordering (shifting a later "Load more" page's contents by one), and `refetchTarget`
 * injecting a target's reports ahead of a later "Load more" page re-returning one of them.
 */
function mergeReportsById(existing: IntelReportDto[], incoming: IntelReportDto[]): IntelReportDto[] {
  const incomingIds = new Set(incoming.map((r) => r.id));
  return [...existing.filter((r) => !incomingIds.has(r.id)), ...incoming];
}

export function IntelHubTab({ worldId, provinceId, reports, onReports, loaded, onLoaded }: IntelHubTabProps) {
  const platform = useAppShellPlatform();
  const { addToast } = useToast();
  const [scope, setScope] = useState<'mine' | 'kingdom'>('mine');
  const [sortKey, setSortKey] = useState<TargetSortValue>('freshest-desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const fetchData = useCallback(async (fetchPage: number, { append }: { append: boolean }) => {
    setLoading(true);
    if (!append) setError(null);
    try {
      const params = { page: fetchPage, size: PAGE_SIZE };
      const response = scope === 'mine'
        ? await getMyIntel(worldId, params)
        : await getKingdomIntel(worldId, params);

      if (append) {
        onReports((prev) => mergeReportsById(prev, response.reports));
      } else {
        onReports(response.reports);
      }
      setTotalCount(response.totalCount);
      setPage(fetchPage);
      onLoaded(true);
    } catch (err) {
      // "Load more" (append: true) follows the same rule as refetchTarget: leave reports/error
      // untouched and toast on the global channel — never bounce the user to a blank/blocking
      // error state as a side effect of a background pagination failure.
      if (append) {
        addToast('Failed to refresh — try again.', 'failure');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load intel');
      }
    } finally {
      setLoading(false);
    }
  }, [worldId, scope, onReports, onLoaded, addToast]);

  // Auto-fetch on mount if not loaded yet
  useEffect(() => {
    if (!loaded) {
      fetchData(0, { append: false });
    }
  }, [loaded, fetchData]);

  // Refetch when scope changes — skips its own mount-time run (React still runs a
  // dependency-array effect once after mount) so mounting already-`loaded` never fires a spurious
  // extra fetch indistinguishable from a real scope change.
  const isFirstScopeEffect = useRef(true);
  useEffect(() => {
    if (isFirstScopeEffect.current) {
      isFirstScopeEffect.current = false;
      return;
    }
    if (loaded) {
      fetchData(0, { append: false });
    }
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetGroups = useMemo(() => groupIntelByTarget(reports), [reports]);
  const crystalEyeReports = useMemo(() => reports.filter((r) => r.type === 'CE'), [reports]);
  const hasMore = (page + 1) * PAGE_SIZE < totalCount;

  function handleShare(reportId: string) {
    const report = reports.find((r) => r.id === reportId);
    shareIntelReport(worldId, reportId)
      .then((updated) => {
        onReports((prev) => prev.map((r) => (r.id === reportId ? updated : r)));
        addToast(`Shared ${report?.targetProvinceName ?? 'report'} with your kingdom`, 'success');
      })
      .catch((err) => {
        addToast(err instanceof Error ? err.message : 'Failed to share report', 'failure');
      });
  }

  function handleDelete(reportId: string) {
    const report = reports.find((r) => r.id === reportId);
    deleteIntelReport(worldId, reportId)
      .then(() => {
        onReports((prev) => prev.filter((r) => r.id !== reportId));
        addToast(`Deleted report on ${report?.targetProvinceName ?? 'target'}`, 'success');
      })
      .catch((err) => {
        addToast(err instanceof Error ? err.message : 'Failed to delete report', 'failure');
      });
  }

  function onLoadMore() {
    fetchData(page + 1, { append: true });
  }

  function onPage(newPage: number) {
    fetchData(newPage, { append: false });
  }

  function onRefresh() {
    fetchData(0, { append: false });
  }

  // Target-scoped refetch (Component 6's onGathered): fetches only this target's reports and
  // replaces only its slice, leaving page/hasMore/every other loaded target untouched.
  //
  // Scope guard: `scopeRef` always reflects the *current* render's scope (updated below, every
  // render), independent of when this callback closure was created — reading the plain `scope`
  // closure variable here instead would compare it against itself (`refetchTarget` is itself
  // recreated whenever `scope` changes, so a closed-over `scope` and `scopeAtCallTime` would
  // always be identical within one invocation, making the guard a no-op).
  const refetchTarget = useCallback((targetProvinceId: string) => {
    const scopeAtCallTime = scopeRef.current;
    const call = scopeAtCallTime === 'mine'
      ? getMyIntel(worldId, { target: targetProvinceId, size: PAGE_SIZE })
      : getKingdomIntel(worldId, { targetProvince: targetProvinceId, size: PAGE_SIZE });

    call
      .then((response) => {
        // Discard a stale cross-scope response — scope has since changed.
        if (scopeRef.current !== scopeAtCallTime) return;
        onReports((prev) => {
          const withoutTarget = prev.filter((r) => r.targetProvinceId !== targetProvinceId);
          return mergeReportsById(withoutTarget, response.reports);
        });
      })
      .catch(() => {
        addToast('Failed to refresh — try again.', 'failure');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldId, scope, onReports, addToast]);

  const shellProps: IntelHubShellProps = {
    worldId,
    provinceId,
    targetGroups,
    crystalEyeReports,
    sortKey,
    onSortKey: setSortKey,
    scope,
    onScope: setScope,
    loading,
    error,
    totalCount,
    hasMore,
    handleShare,
    handleDelete,
    onLoadMore,
    onRefresh,
    refetchTarget,
    page,
    onPage,
  };

  return platform === 'mobile'
    ? <MobileIntelHubShell {...shellProps} />
    : <DesktopIntelHubShell {...shellProps} />;
}
